/**
 * AddressSocialDetail Component
 *
 * Description: This component renders detailed profile information including contact details, gender, important dates,
 * and social media links.
 *
 * File Name: AddressSocialDetail.tsx
 * Date: 12-08-2024
 */

import React, { useState } from "react";
import {
  faClockRotateLeft,
  faEnvelope,
  faLocationDot,
  faPhone,
  faEnvelopesBulk,
  faVenusMars,
  faCakeCandles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookSquare,
  faInstagram,
  faLinkedin,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { IProfileData } from "src/types/GlobalInterfaces";
import moment from "moment";
import { truncateString } from "src/helpers/helperFunctions";
import shareIcon from "src/assets/images/share-icon.png";
import modalCloseIcon from "src/assets/images/share-modal-close-icon.svg";
import facebookIcon from "src/assets/images/facebookshare.svg";
import instaIcon from "src/assets/images/instagramshare.svg";
import twitterIcon from "src/assets/images/twittershare.svg";
import whatsappIcon from "src/assets/images/share-whatsapp-icon.svg";
import linkdinIcon from "src/assets/images/linkedinshare.svg";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";
import { admin, doctor, operator, superAdmin } from "src/lib/constans";
interface IProfileProps {
  data: IProfileData;
}
interface SocialIconsCopyProps {
  facebookIcon: string;
  twitterIcon: string;
  whatsappIcon: string;
  linkedinIcon: string;
}

const AddressSocialDetail: React.FC<IProfileProps> = ({ data = {} }) => {
  console.log(data, "datadatadatadatadata");
  const { userDetails } = useSelector((state: RootState) => state.appReducer);
  const [modalVisible, setModalVisible] = useState(false);
  const profileLink = `${process.env.REACT_APP_WEBSITE_URL}${
    data?.userRole === "customer"
      ? "/patient-public/"
      : "/doctor-public-profile/"
  }${data?._id}/view`;
  // const profileLink = `${`https://dashboard.shifara.com`}${data?.userRole ==="customer" ?  "/patient-public/": "/doctor-public-profile/"}${data?._id}/view`;
  const handleCopy = () => {
    if (!profileLink) {
      toast.error("Profile link is not available!", {
        autoClose: 3000,
      });
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern Clipboard API
      navigator.clipboard
        .writeText(profileLink)
        .then(() => {
          toast.success("Link copied to clipboard!", {
            autoClose: 3000,
          });
        })
        .catch((err) => {
          console.error("Clipboard copy failed: ", err);
          fallbackCopyText(profileLink);
        });
    } else {
      // Fallback for older browsers
      fallbackCopyText(profileLink);
    }
  };

  const fallbackCopyText = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; // Avoid scrolling to bottom
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        toast.success("Link copied to clipboard!", {
          autoClose: 3000,
        });
      } else {
        throw new Error("Fallback copy failed");
      }
    } catch (err) {
      console.error("Fallback copy failed: ", err);
      toast.error("Failed to copy the link!", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      document.body.removeChild(textArea);
    }
  };
  const handleShare = (platform: string) => {
    let shareURL = "";
    const currentURL = window.location.href; // Current page URL

    switch (platform) {
      case "facebook":
        shareURL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          currentURL
        )}`;
        break;
      case "twitter":
        shareURL = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          currentURL
        )}&text=Check%20this%20out!`;
        break;
      case "whatsapp":
        shareURL = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          currentURL
        )}`;
        break;
      case "linkedin":
        shareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          currentURL
        )}`;
        break;
      default:
        break;
    }

    if (shareURL) {
      window.open(shareURL, "_blank", "noopener,noreferrer");
    }
  };

  const printSocialLinks = () => {
    return (
      <>
            <a
              href={data?.linkedinLink || ""}
              target="_blank"
              className={`theme-color-green w-6 lg:w-[1.8vw] ${data?.linkedinLink ? "scale-100" : "scale-0"}`}
              id="linkedin-icon"
            >
              <FontAwesomeIcon icon={faLinkedin} className="w-full h-full hover:text-[#0077B5]" />
            </a>
            <a
              href={data?.facebookLink || ""}
              target="_blank"
              className={`theme-color-green w-6 lg:w-[1.8vw] ${data?.facebookLink ? "scale-100" : "scale-0"}`}
              id="facebook-icon"
            >
              <FontAwesomeIcon icon={faFacebookSquare} className="w-full h-full hover:text-[#1877F2]" />
            </a>
            <a
              href={data?.instagramLink || ""}
              target="_blank"
              className={`theme-color-green w-6 lg:w-[1.8vw] ${data?.instagramLink ? "scale-100" : "scale-0"}`}
              id="instagram-icon"
            >
              <FontAwesomeIcon icon={faInstagram} className="w-full h-full hover:text-[#C13584]" />
            </a>
            <a
              href={data?.whatsappLink || ""}
              target="_blank"
              className={`theme-color-green w-6 lg:w-[1.8vw] ${data?.whatsappLink ? "scale-100" : "scale-0"}`}
              id="whatsapp-icon"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="w-full h-full hover:text-[#25D366]" />
            </a>
      </>
    );
  };
  return (
    <>
      <div className="">
        <div className="parent-box p-4 rounded-lg bg-white">
          <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white">
            <FontAwesomeIcon className="w-8" icon={faPhone} />
            <span className="text-left truncate">
              {data?.mobile || data?.number || "-"}
            </span>
          </button>
          <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white">
            <FontAwesomeIcon className="w-8" icon={faEnvelope} />
            <span className="text-left truncate">{data?.email || "-"}</span>
          </button>
          {data?.address && (
            <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white">
              <FontAwesomeIcon className="w-8" icon={faLocationDot} />
              <span title={data?.address} className="text-left">
                {truncateString(data?.address || "", 25) || "-"}
              </span>
            </button>
          )}
          <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white capitalize">
            <FontAwesomeIcon className="w-8" icon={faVenusMars} />
            <span className="text-left truncate">{data?.gender || "-"}</span>
          </button>
          <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white">
            {/* <FontAwesomeIcon className="w-8" icon={faEnvelopesBulk} /> */}
            <FontAwesomeIcon className="w-8" icon={faCakeCandles} />
            <span className="text-left truncate">
              {moment(data?.date).format("DD-MM-YYYY") || "-"}
            </span>
          </button>
          <button className="border-[#202020] text-[#202020] border w-full profile-right-side-btn flex items-center font-size-16px rounded-lg font-Poppins-Medium mb-2 bg-white">
            <FontAwesomeIcon className="w-8" icon={faClockRotateLeft} />
            <span className="text-left truncate">
              User Since {moment(data?.createdAt).format("DD MMM, YYYY") || "-"}
            </span>
          </button>

          {(data?.linkedinLink ||
            data?.facebookLink ||
            data?.instagramLink ||
            data?.whatsappLink) &&
            (userDetails?.userRole === operator ||
              userDetails?.userRole === superAdmin) && (
              <div className="flex gap-1 mt-2 justify-between">
                {printSocialLinks()}
              </div>
            )}
        </div>
        {(data?.linkedinLink ||
          data?.facebookLink ||
          data?.instagramLink ||
          data?.whatsappLink) &&
          userDetails?.userRole === doctor && (
            <div className="flex flex-col gap-1 mt-2">
              <div
                className="flex parent-box py-2 justify-between"
                style={{ borderRadius: "7px" }}
              >
                {printSocialLinks()}
              </div>
            </div>
          )}
        <div className="flex flex-row justify-end">
          <div
            onClick={() => setModalVisible(true)}
            id="share-icon-img"
            className="share-icon-img w-fit "
          >
            <img src={shareIcon} alt="" />
            <span className="font-size-16px font-Poppins-Medium theme-color-green">
              Share
            </span>
          </div>
        </div>
      </div>
      {modalVisible && (
        <div className="share-modal-overlay">
          <div className="absolute z-20 share-link-modal">
            <button
              onClick={() => setModalVisible(false)}
              className="absolute -top-2 font-size-24px "
              style={{ right: "-10px" }}
              id="share-modal-close-btn"
            >
              <img src={modalCloseIcon} alt="" width="20px" />
            </button>
            <div>
              <p className="font-size-28px font-Poppins-Bold theme-color-green text-center">
                Share
              </p>
            </div>
            <div className="social-icons-copy">
              <div
                className="social-icons-copy-inner"
                onClick={() => handleShare("facebook")}
                style={{ cursor: "pointer" }}
                id="share-modal-facebook-icon"
              >
                <img src={facebookIcon} alt="Share on Facebook" />
                <span className="text-[#707070] font-size-12px font-Poppins-Medium">
                  Facebook
                </span>
              </div>
              <div
                className="social-icons-copy-inner"
                onClick={() => handleShare("twitter")}
                style={{ cursor: "pointer" }}
                id="share-modal-twitter-icon"
              >
                <img src={twitterIcon} alt="Share on Twitter" />
                <span className="text-[#707070] font-size-12px font-Poppins-Medium">
                  Twitter
                </span>
              </div>
              <div
                className="social-icons-copy-inner"
                onClick={() => handleShare("whatsapp")}
                style={{ cursor: "pointer" }}
                id="share-modal-whatsapp-icon"
              >
                <img src={whatsappIcon} alt="Share on WhatsApp" />
                <span className="text-[#707070] font-size-12px font-Poppins-Medium">
                  WhatsApp
                </span>
              </div>
              <div
                className="social-icons-copy-inner"
                onClick={() => handleShare("linkedin")}
                style={{ cursor: "pointer" }}
                id="share-modal-linkedin-icon"
              >
                <img src={linkdinIcon} alt="Share on LinkedIn" />
                <span className="text-[#707070] font-size-12px font-Poppins-Medium">
                  LinkedIn
                </span>
              </div>
            </div>
            <div className="copy-socials-links pb-4">
              <input
                className="font-size-16px font-Poppins-Medium"
                type="text"
                value={profileLink}
                readOnly
                id="share-modal-input"
              />
              <button
                className="theme-bg-green font-Poppins-Medium font-size-14px"
                onClick={handleCopy}
                id="share-modal-copy-btn"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddressSocialDetail;
